/**
 * Client-side gates for AI Brief file attachments (NPJB-369, AC2/AC3/AC5/AC6).
 *
 * The backend (`POST /audience/brief/parse-files`) enforces all four checks
 * independently and authoritatively — this layer exists only because the ACs
 * require rejection *before* upload, not because the server can be trusted to
 * skip its own validation.
 *
 * Pure, framework-free functions: no React, no CSS. `applyBriefFileGates` is the
 * entry point the composer UI calls — it takes the newly-picked files plus the
 * already-staged ones and hands back the files to keep plus at most one error
 * message to show (never a list — the UI has room for a single line).
 */

export const ALLOWED_BRIEF_FILE_EXTENSIONS = [
  '.xlsx',
  '.xls',
  '.pptx',
  '.ppt',
  '.docx',
  '.doc',
  '.pdf',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
] as const;

export const MAX_BRIEF_FILES = 5;
export const MAX_BRIEF_FILE_SIZE_BYTES = 26_214_400; // 25 MB
// Backend's `upload.brief.request_budget_bytes` (35 MB) — the whole multipart
// request, not any one file. Exported so the composer toolbar can render a
// running total against the same number the server will enforce.
export const MAX_BRIEF_TOTAL_SIZE_BYTES = 36_700_160;

export const UNSUPPORTED_FILE_TYPE_MESSAGE =
  'Unsupported file type. Please upload an Excel, PowerPoint, Word, PDF, text, or image file.';
export const TOO_MANY_FILES_MESSAGE = 'You can upload up to 5 files at a time.';
export const FILE_TOO_LARGE_MESSAGE = 'File is too large. Please upload files under 25 MB.';
export const TOTAL_SIZE_TOO_LARGE_MESSAGE =
  'These files are too large to process together. Please upload fewer or smaller files.';
export const emptyFileMessage = (filename: string): string =>
  `${filename} is empty. Please upload a file with content.`;

export interface BriefFileGateResult {
  /** Files that passed every gate, truncated to the remaining slot count. */
  accepted: File[];
  /** At most one message, highest-priority reason first: type, count, size, empty. */
  error: string | null;
}

const extensionOf = (filename: string): string => {
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex === -1 ? '' : filename.slice(dotIndex).toLowerCase();
};

export const isAllowedBriefFileExtension = (filename: string): boolean =>
  (ALLOWED_BRIEF_FILE_EXTENSIONS as readonly string[]).includes(extensionOf(filename));

export const isBriefFileTooLarge = (file: File): boolean => file.size > MAX_BRIEF_FILE_SIZE_BYTES;

export const isBriefFileEmpty = (file: File): boolean => file.size === 0;

interface ClassifiedBriefFiles {
  /** Files whose extension isn't in the allow-list. */
  invalidTypeFiles: File[];
  /** Type-valid files that are 0 bytes. */
  emptyFiles: File[];
  /** Type-valid files over the per-file size cap. */
  tooLargeFiles: File[];
  /** Type-valid files that are neither empty nor too large. */
  goodFiles: File[];
}

/**
 * Buckets a batch of newly-picked files by which per-file gate (if any) they
 * trip. Type is checked first — an invalid-type file never also competes for
 * the empty/size buckets — then every type-valid file lands in exactly one
 * of empty, too-large, or good (empty and too-large are mutually exclusive:
 * a 0-byte file can never also exceed the size cap).
 */
const classifyBriefFiles = (newFiles: readonly File[]): ClassifiedBriefFiles => {
  const invalidTypeFiles: File[] = [];
  const emptyFiles: File[] = [];
  const tooLargeFiles: File[] = [];
  const goodFiles: File[] = [];

  for (const file of newFiles) {
    if (!isAllowedBriefFileExtension(file.name)) {
      invalidTypeFiles.push(file);
    } else if (isBriefFileEmpty(file)) {
      emptyFiles.push(file);
    } else if (isBriefFileTooLarge(file)) {
      tooLargeFiles.push(file);
    } else {
      goodFiles.push(file);
    }
  }

  return { invalidTypeFiles, emptyFiles, tooLargeFiles, goodFiles };
};

interface BriefFileGateErrorInputs {
  invalidTypeFiles: readonly File[];
  hasTooMany: boolean;
  tooLargeFiles: readonly File[];
  exceedsTotalBudget: boolean;
  emptyFiles: readonly File[];
}

/**
 * Picks the single highest-priority error to show — type > count > per-file
 * size > total budget > empty, per the ordering documented on
 * `applyBriefFileGates`.
 *
 * Each rule pairs a pre-computed boolean with a message *thunk*. `Array#find`
 * only reads the boolean at index 0 while scanning, so a later rule's thunk
 * (notably `emptyFileMessage(emptyFiles[0].name)`) is never invoked while
 * searching; only the matched rule's thunk is called, via `?.[1]()`, once the
 * match is found. That's what keeps `emptyFiles[0].name` from being touched
 * when `emptyFiles` is empty.
 */
const selectBriefFileGateError = ({
  invalidTypeFiles,
  hasTooMany,
  tooLargeFiles,
  exceedsTotalBudget,
  emptyFiles,
}: BriefFileGateErrorInputs): string | null => {
  const rules: readonly [boolean, () => string][] = [
    [invalidTypeFiles.length > 0, () => UNSUPPORTED_FILE_TYPE_MESSAGE],
    [hasTooMany, () => TOO_MANY_FILES_MESSAGE],
    [tooLargeFiles.length > 0, () => FILE_TOO_LARGE_MESSAGE],
    [exceedsTotalBudget, () => TOTAL_SIZE_TOO_LARGE_MESSAGE],
    [emptyFiles.length > 0, () => emptyFileMessage(emptyFiles[0].name)],
  ];
  return rules.find(([matches]) => matches)?.[1]() ?? null;
};

/**
 * Gates a batch of newly-picked files against the already-staged list.
 *
 * Per-file checks (type, empty, size) silently drop the offending file from
 * `accepted`; the batch-level count check truncates whatever remains to the
 * number of open slots. Only one error message is ever returned, chosen by
 * priority — type > count > per-file size > total budget > empty — so the UI
 * never has to decide which of several simultaneous problems to show.
 *
 * The total-budget check sits right after per-file size and before empty on
 * purpose: type and count are hard structural problems (this file can never
 * be used; this batch can never fit) that are cheapest to explain and fix
 * first. Per-file size and total budget are then grouped together — both are
 * "these bytes don't fit" messages — with per-file size taking priority
 * because it names a single culprit file, which is more actionable than a
 * batch-level total. Empty stays lowest priority, as before.
 */
export const applyBriefFileGates = (
  newFiles: readonly File[],
  stagedFiles: readonly File[],
): BriefFileGateResult => {
  const { invalidTypeFiles, emptyFiles, tooLargeFiles, goodFiles } = classifyBriefFiles(newFiles);
  // Count is checked against every type-valid file (not just the size-valid
  // ones) so a batch that is already at (or over) the cap reports "too many"
  // even when the overflowing file also happens to be oversized/empty — the
  // cap was reached independent of that file's size.
  const typedFileCount = newFiles.length - invalidTypeFiles.length;

  const remainingSlots = Math.max(0, MAX_BRIEF_FILES - stagedFiles.length);
  const withinCountCap = goodFiles.slice(0, remainingSlots);
  const hasTooMany = stagedFiles.length + typedFileCount > MAX_BRIEF_FILES;

  const stagedTotalBytes = stagedFiles.reduce((sum, file) => sum + file.size, 0);
  const acceptedTotalBytes = withinCountCap.reduce((sum, file) => sum + file.size, 0);
  const exceedsTotalBudget = stagedTotalBytes + acceptedTotalBytes > MAX_BRIEF_TOTAL_SIZE_BYTES;

  // A batch that would blow the request budget is rejected outright — none of
  // its files get staged — rather than greedily accepting a subset that still
  // fits. Partial acceptance would silently reorder which files "win" a slot
  // and still leave the user re-picking to find out which; a flat rejection
  // with the exact server-facing reason is simpler to act on.
  const accepted = exceedsTotalBudget ? [] : withinCountCap;

  const error = selectBriefFileGateError({
    invalidTypeFiles,
    hasTooMany,
    tooLargeFiles,
    exceedsTotalBudget,
    emptyFiles,
  });

  return { accepted, error };
};
